import { useState, useEffect } from "react";
import { useAuth, api } from "@/App";
import CustomerLayout from "@/components/layout/CustomerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { User, Mail, Phone, Bell, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const Settings = () => {
  const { user, logout } = useAuth();
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    phone: user?.phone || ""
  });
  const [saving, setSaving] = useState(false);
  const [notifications, setNotifications] = useState({
    email_points: true,
    email_vouchers: true,
    email_promos: false
  });

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/customer/profile", profileData);
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomerLayout title="Settings">
      <div className="space-y-6 animate-fade-in max-w-2xl">
        {/* Profile Section */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <User size={20} className="text-[#D4AF37]" />
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#A0A0A0]">Full Name</Label>
                <Input
                  id="name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="bg-[#1A1A1E] border-[#2A2A2F] text-white focus:border-[#D4AF37]"
                  data-testid="settings-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#A0A0A0]">Email</Label>
                <div className="relative">
                  <Input
                    id="email"
                    value={user?.email || ""}
                    disabled
                    className="bg-[#1A1A1E] border-[#2A2A2F] text-[#6B6B6B] pl-10"
                  />
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
                </div>
                <p className="text-[#6B6B6B] text-xs">Email cannot be changed</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#A0A0A0]">Phone Number</Label>
                <div className="relative">
                  <Input
                    id="phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    className="bg-[#1A1A1E] border-[#2A2A2F] text-white pl-10 focus:border-[#D4AF37]"
                    data-testid="settings-phone-input"
                  />
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={saving}
                className="bg-gradient-to-r from-[#D4AF37] to-[#B8942D] text-[#0A0A0B] font-semibold"
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Notification Preferences */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Bell size={20} className="text-[#D4AF37]" />
              Notification Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-[#2A2A2F]">
              <div>
                <p className="text-white font-medium">Points Earned</p>
                <p className="text-[#6B6B6B] text-sm">Receive emails when you earn miles</p>
              </div>
              <Switch
                checked={notifications.email_points}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email_points: checked })}
                data-testid="notification-points-switch"
              />
            </div>
            
            <div className="flex items-center justify-between py-3 border-b border-[#2A2A2F]">
              <div>
                <p className="text-white font-medium">Voucher Updates</p>
                <p className="text-[#6B6B6B] text-sm">Expiry reminders and new vouchers</p>
              </div>
              <Switch
                checked={notifications.email_vouchers}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email_vouchers: checked })}
                data-testid="notification-vouchers-switch"
              />
            </div>
            
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-white font-medium">Promotions</p>
                <p className="text-[#6B6B6B] text-sm">Special offers and promotions</p>
              </div>
              <Switch
                checked={notifications.email_promos}
                onCheckedChange={(checked) => setNotifications({ ...notifications, email_promos: checked })}
                data-testid="notification-promos-switch"
              />
            </div>
          </CardContent>
        </Card>

        {/* Account Section */}
        <Card className="bg-[#131316] border-[#2A2A2F]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Lock size={20} className="text-[#D4AF37]" />
              Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-[#2A2A2F]">
              <div>
                <p className="text-white font-medium">Member Since</p>
                <p className="text-[#6B6B6B] text-sm">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={logout}
              className="w-full border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white"
              data-testid="logout-settings-btn"
            >
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </CustomerLayout>
  );
};

export default Settings;
